import { Box, SxProps, Theme } from "@mui/material";
import { keyframes } from "@mui/system";
import { SPEAKING_BARS } from "../telehealth-v2-constant";

// Emotion hashes the name, so interpolate it rather than referencing a
// literal `@keyframes` declared in a different sx block — that only resolves
// by accident of stylesheet ordering.
const speakingBar = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
`;

export interface SpeakingIndicatorProps {
  /** Accessible label, e.g. the speaker's name. */
  label?: string;
  /** Bar height in px. */
  size?: number;
  sx?: SxProps<Theme>;
}

/**
 * Equaliser bars shown while a participant is talking. Purely decorative —
 * the caller decides when to mount it.
 */
export default function SpeakingIndicator({
  label,
  size = 12,
  sx,
}: Readonly<SpeakingIndicatorProps>) {
  return (
    <Box
      component="span"
      role="img"
      aria-label={label ? `${label} is speaking` : "Speaking"}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          height: size,
          flexShrink: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {SPEAKING_BARS.map((bar) => (
        <Box
          key={bar.id}
          component="span"
          sx={{
            width: "2px",
            height: "100%",
            borderRadius: "1px",
            bgcolor: "success.main",
            transformOrigin: "center",
            animation: `${speakingBar} 700ms ease-in-out infinite`,
            animationDelay: `${bar.delayMs}ms`,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              transform: "scaleY(0.7)",
            },
          }}
        />
      ))}
    </Box>
  );
}
